import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";

import { CihClosingCta } from "~/components/cih/closing-cta";
import {
  CihCoachCellGrid,
  toCihCoachCell,
  type CihCoachCellData,
} from "~/components/cih/coach-cell";
import { CihPagination } from "~/components/cih/pagination";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  getCoachesByFrameworkFn,
  getPublishedFrameworkWithRelationsFn,
  type FrameworkWithRelations,
} from "~/lib/functions/cih/frameworks";
import { createBrandMeta } from "~/lib/shared/meta";

const PAGE_SIZE = 24;

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
});

type LoaderData = {
  title: string;
  framework: FrameworkWithRelations;
  coaches: CihCoachCellData[];
  total: number;
  page: number;
};

export const Route = createFileRoute("/_public/frameworks/$slug/coaches")({
  staticData: { title: "Coaches" },
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ params, deps }): Promise<LoaderData> => {
    const framework = await getPublishedFrameworkWithRelationsFn({
      data: { slug: params.slug },
    });
    if (!framework) throw notFound();
    const result = await getCoachesByFrameworkFn({
      data: {
        frameworkId: framework.id,
        page: deps.page,
        limit: PAGE_SIZE,
      },
    });
    return {
      title: `${framework.name} coaches`,
      framework,
      coaches: result.coaches.map(toCihCoachCell),
      total: result.total,
      page: result.page,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const name = loaderData?.framework.name ?? params.slug;
    const page = loaderData?.page ?? 1;
    const pageSuffix = page > 1 ? ` — Page ${page}` : "";
    return createBrandMeta({
      title: `${name} coaches${pageSuffix} | Dream Coach Match`,
      description: `All coaches who use ${name}.`,
      ogUrl: `https://dreamcoachmatch.com/frameworks/${params.slug}/coaches`,
      canonical: `https://dreamcoachmatch.com/frameworks/${params.slug}/coaches`,
    });
  },
  component: FrameworkCoachesPage,
});

function FrameworkCoachesPage() {
  const { framework, coaches, total, page } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const buildHref = (p: number) =>
    p === 1
      ? `/frameworks/${slug}/coaches`
      : `/frameworks/${slug}/coaches?page=${p}`;

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 pb-12 md:pt-10 md:pb-16 xl:px-0">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Frameworks", href: "/frameworks" },
              { label: framework.name, href: `/frameworks/${slug}` },
              { label: "Coaches" },
            ]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
            ● {framework.name}
          </div>
          <h1 className="mt-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl lg:text-5xl xl:text-6xl">
            Coaches who use {framework.name}
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <Link
              to="/frameworks/$slug/training-programs"
              params={{ slug }}
              className="font-medium text-purple-700 hover:text-purple-800"
            >
              See training programs that teach {framework.name} →
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20 xl:px-0">
        <CihCoachCellGrid coaches={coaches} />
      </div>

      <CihPagination
        page={page}
        totalPages={totalPages}
        buildHref={buildHref}
      />

      <CihClosingCta
        headline={`Work with a coach who uses ${framework.name}.`}
        cta={
          <Link
            to="/discovery"
            search={{ source: `framework:${slug}:coaches` }}
          >
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
